import * as React from "react";
import { useLocation, useSearch } from "wouter";
import {
  ArrowLeft, ArrowRight, Camera, Check, CheckCircle2, Loader2, MapPin, Package,
  PackageCheck, RotateCcw, Sparkles, X, ListChecks, AlertTriangle,
} from "lucide-react";
import { apiFetch } from "@/lib/api.js";
import { useToast } from "@/components/Toast.js";
import { useSpaces } from "@/hooks/useSpaces.js";
import { useFeatureFlags } from "@/contexts/FeatureFlagContext.js";
import { InventoryPicker, type PickerItem } from "@/features/inventory/InventoryPicker.js";
import { cn } from "@/lib/utils.js";
import { MovesToMake } from "./MovesToMake.js";

/**
 * TriagePage — the P0-D flagship triage slice.
 *
 * A user starts "sort out this room" with NO prior catalog (R-D1), scans (or
 * picks items by hand — R-D10), and the AI returns each item PRE-SORTED into one
 * of three buckets (R-D4) which the user corrects one tap at a time (R-D6). The
 * whole space is NEVER shown at once (R-D11); we present one item at a time with
 * a position rail — no percentage, no denominator, no health score (R-D12).
 *
 * ❌ ("doesn't belong") is resolved BATCHED, never per-item in the moment (R-D8):
 * a known destination → a decided move; unsure → flagged Re-locate (P0-B).
 * Completing a pass RECORDS a triage pass (R-C6) — photo, timestamp,
 * items+buckets, optional free-text label — and celebrates the PASS (R-C8),
 * never the space. Returning shows a resume hint (R-C7).
 *
 * Decided vs. done (docs/organize-declutter-onboarding-design.md §6b): choosing
 * where something goes does NOT move it. Each destination defaults to "Sure" —
 * a planned move that waits in "Moves to make" until the user confirms they've
 * moved it — or the user opts into "Try it", which starts a normal 2-week trial.
 * Item locations stay honest until something is actually moved.
 *
 * "Triage" is the internal/code name only; users see "Sort" (§8).
 */

type Bucket = "belongs" | "wrong_container" | "doesnt_belong";
// "moves" is the post-record step showing this sort's Moves to make. It's never
// autosaved (autosave stops once the pass is recorded).
type Phase = "space" | "context" | "capture" | "sort" | "resolve" | "done" | "moves";

interface TriageItem {
  itemId: string;
  name: string;
  bucket: Bucket;
  suggestedContainer?: string;
  suggestedContainerSpaceId?: string | null;
  /** For ❌ resolution: chosen destination, or "unsure" to flag Re-locate. */
  resolveSpaceId?: string | null;
  resolveUnsure?: boolean;
}

const CONTEXT_CHIPS = ["Too cluttered", "Can't find things", "Wrong stuff here", "Just tidying"];

// User-facing labels match mobile (§8). "Wrong spot" works whether the space
// being sorted is a room or a container inside one.
const BUCKET_META: Record<Bucket, { label: string; hint: string }> = {
  belongs: { label: "Belongs here", hint: "Stays put — just confirm it." },
  wrong_container: { label: "Wrong spot", hint: "Belongs here, just in a different spot inside." },
  doesnt_belong: { label: "Doesn't belong", hint: "Sort out where it goes at the end." },
};

interface DestinationGroup { spaceId: string; name: string; itemIds: string[] }

export default function TriagePage() {
  const [, navigate] = useLocation();
  const search = useSearch();
  const toast = useToast();
  const { spaces } = useSpaces();
  const { aiEnabled } = useFeatureFlags();
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const spaceIdFromUrl = new URLSearchParams(search).get("spaceId");
  const [spaceId, setSpaceId] = React.useState<string>(spaceIdFromUrl ?? "");
  const [phase, setPhase] = React.useState<Phase>(spaceIdFromUrl ? "context" : "space");

  // Context (optional, skippable — R-D9).
  const [contextChips, setContextChips] = React.useState<string[]>([]);
  const [contextText, setContextText] = React.useState("");

  // Capture / analysis.
  const [analyzing, setAnalyzing] = React.useState(false);
  // Photo storage key(s) that seeded the sort, if any. Carried so a resumed
  // draft round-trips the same photos (see resumeDraft). Empty for a no-scan sort.
  const [photoKeys, setPhotoKeys] = React.useState<string[]>([]);
  const [noScan, setNoScan] = React.useState(false);
  const [pickIds, setPickIds] = React.useState<string[]>([]);
  const [pickItems, setPickItems] = React.useState<PickerItem[]>([]);

  // Sort.
  const [items, setItems] = React.useState<TriageItem[]>([]);
  const [cursor, setCursor] = React.useState(0);
  const [alreadySorted, setAlreadySorted] = React.useState<Set<string>>(new Set());

  // Resume hint.
  const [lastPass, setLastPass] = React.useState<{ label: string | null; createdAt: string } | null>(null);

  // Interrupted-triage draft (server-side). An OPEN session the user can resume
  // exactly where they left off — distinct from `lastPass` (a hint about the
  // last COMPLETED pass). Null = no open draft for this space.
  interface TriageSession {
    phase: Phase; cursor: number; context: string | null;
    photoKeys: string[] | null; label: string | null; items: TriageItem[]; updatedAt: string;
  }
  const [resumable, setResumable] = React.useState<TriageSession | null>(null);
  // Once the user resumes (or explicitly starts over) we stop offering it.
  const [resumeHandled, setResumeHandled] = React.useState(false);
  // Guards autosave: don't persist a draft while restoring one, or after the
  // sort has completed into a pass (which deletes the draft server-side).
  const restoringRef = React.useRef(false);

  // Pass completion.
  const [passLabel, setPassLabel] = React.useState("");
  const [recording, setRecording] = React.useState(false);
  const [saved, setSaved] = React.useState(false);
  // Destinations the user opted to "Try it" (2-week trial). Everything else is
  // "Sure" — a planned move (§6b).
  const [tryDestinations, setTryDestinations] = React.useState<Set<string>>(new Set());
  // Item IDs this sort put into "Moves to make" (shown in the "moves" phase).
  const [plannedItemIds, setPlannedItemIds] = React.useState<string[]>([]);
  const [triedCount, setTriedCount] = React.useState(0);
  // Where to go after the "moves" step (Done → Organize, or sort another part).
  const [afterMoves, setAfterMoves] = React.useState<(() => void) | null>(null);

  const space = spaces.find((s) => s.id === spaceId);
  const siblingContainers = React.useMemo(
    () => spaces.filter((s) => s.parentId === spaceId),
    [spaces, spaceId]
  );
  const placeableSpaces = spaces.filter((s) => s.type !== "home");
  const contextString = [...contextChips, contextText.trim()].filter(Boolean).join("; ") || undefined;

  // Load the resume hint + already-triaged item set + any OPEN in-progress draft
  // when a space is chosen (R-C1/C7 + interrupted-triage persistence).
  React.useEffect(() => {
    if (!spaceId) return;
    setResumable(null);
    setResumeHandled(false);
    apiFetch<{ id: string; label: string | null; createdAt: string }[]>(`/api/spaces/${spaceId}/triage-passes`)
      .then((rows) => setLastPass(rows[0] ? { label: rows[0].label, createdAt: rows[0].createdAt } : null))
      .catch(() => setLastPass(null));
    apiFetch<{ itemIds: string[] }>(`/api/spaces/${spaceId}/triaged-items`)
      .then((r) => setAlreadySorted(new Set(r.itemIds)))
      .catch(() => setAlreadySorted(new Set()));
    // Fetch the open draft (interrupted sort) so we can offer to resume it.
    apiFetch<TriageSession | null>(`/api/spaces/${spaceId}/triage-session`)
      .then((s) => setResumable(s && s.items?.length ? s : null))
      .catch(() => setResumable(null));
  }, [spaceId]);

  // --- Autosave the in-progress draft (server-side) so an interrupted sort is
  // resumable. Debounced; only runs once a sort is underway, never while
  // restoring a draft or after the pass has been recorded (which clears it).
  React.useEffect(() => {
    if (restoringRef.current || saved) return;
    if (!spaceId || items.length === 0) return;
    if (phase !== "sort" && phase !== "resolve" && phase !== "done") return;
    const t = setTimeout(() => {
      apiFetch(`/api/triage-sessions`, {
        method: "PUT",
        body: JSON.stringify({
          spaceId, phase, cursor,
          context: contextString ?? null,
          photoKeys: photoKeys.length ? photoKeys : null,
          label: passLabel.trim() || null,
          items: items.map((i) => ({
            itemId: i.itemId, name: i.name, bucket: i.bucket,
            suggestedContainer: i.suggestedContainer,
            suggestedContainerSpaceId: i.suggestedContainerSpaceId ?? null,
            resolveSpaceId: i.resolveSpaceId ?? null,
            resolveUnsure: i.resolveUnsure ?? false,
          })),
        }),
      }).catch(() => { /* best-effort draft; a failed save just means no resume */ });
    }, 600);
    return () => clearTimeout(t);
  }, [spaceId, phase, cursor, items, passLabel, contextString, photoKeys, saved]);

  // Resume the open draft — restore items/cursor/phase/label AND the entry
  // context + photo keys, so the first post-resume autosave doesn't blank them
  // (the payload is rebuilt from this state). Context was saved as the chips and
  // free text joined by "; "; split it back so the chip UI reflects it and the
  // round-trip is lossless.
  const resumeDraft = () => {
    if (!resumable) return;
    restoringRef.current = true;
    setItems(resumable.items);
    setCursor(Math.min(resumable.cursor, Math.max(0, resumable.items.length - 1)));
    setPassLabel(resumable.label ?? "");
    setPhotoKeys(resumable.photoKeys ?? []);
    // Restore context: known preset chips light up again; anything else becomes
    // free text, preserving exactly what contextString would re-serialize.
    const parts = (resumable.context ?? "").split(";").map((p) => p.trim()).filter(Boolean);
    setContextChips(parts.filter((p) => CONTEXT_CHIPS.includes(p)));
    setContextText(parts.filter((p) => !CONTEXT_CHIPS.includes(p)).join("; "));
    setNoScan(false);
    setSaved(false);
    setPhase(resumable.phase);
    setResumeHandled(true);
    // Release the autosave guard after the state settles so subsequent edits save.
    setTimeout(() => { restoringRef.current = false; }, 0);
  };

  // Discard the open draft and start fresh (server-side delete). Only offered in
  // the entry phases (space/context/capture) where `items` is empty, so the
  // autosave effect is inert and no in-flight PUT can re-create what we delete.
  const discardDraft = () => {
    setResumeHandled(true);
    setResumable(null);
    if (spaceId) apiFetch(`/api/spaces/${spaceId}/triage-session`, { method: "DELETE" }).catch(() => {});
  };

  const goBack = () => navigate("/organize");

  // --- Capture: photo → AI triage → create-or-match to get real item IDs ---
  const handlePhoto = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !spaceId) return;
    setAnalyzing(true);
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const base64 = (reader.result as string).split(",")[1];
        // 1. AI pre-sorts detected items into buckets.
        const analysis = await apiFetch<{ items: { name: string; bucket: Bucket; suggestedContainer?: string }[] }>(
          "/api/ai/analyze-triage",
          { method: "POST", body: JSON.stringify({ imageBase64: base64, mimeType: file.type, spaceId, userContext: contextString }) },
        );
        if (analysis.items.length === 0) {
          toast.show("Couldn't spot any items — try another photo, or pick items by hand.", "warning");
          setAnalyzing(false);
          return;
        }
        // 2. Create-or-match detected names to real items in the space (R-D3, F1)
        //    so each bucket decision has a real itemId to record.
        const resolved = await apiFetch<{ matched: { name: string; itemId: string }[]; created: { id: string; name: string }[] }>(
          "/api/items/resolve-scan",
          { method: "POST", body: JSON.stringify({ detected: analysis.items.map((i) => ({ name: i.name })), targetSpaceId: spaceId, create: true }) },
        );
        const idByName = new Map<string, string>();
        for (const m of resolved.matched) idByName.set(m.name.trim().toLowerCase(), m.itemId);
        for (const c of resolved.created) idByName.set(c.name.trim().toLowerCase(), c.id);

        const triaged: TriageItem[] = analysis.items
          .map((i) => {
            const itemId = idByName.get(i.name.trim().toLowerCase());
            if (!itemId) return null;
            const sib = i.suggestedContainer
              ? siblingContainers.find((s) => s.name.trim().toLowerCase() === i.suggestedContainer!.trim().toLowerCase())
              : undefined;
            return {
              itemId,
              name: i.name,
              bucket: i.bucket,
              suggestedContainer: i.suggestedContainer,
              suggestedContainerSpaceId: sib?.id ?? null,
            } as TriageItem;
          })
          .filter((x): x is TriageItem => x !== null)
          // R-C1: exclude items already sorted in a prior pass of this space.
          .filter((x) => !alreadySorted.has(x.itemId));

        if (triaged.length === 0) {
          toast.show("Everything here was already sorted last time.", "success");
          setAnalyzing(false);
          return;
        }
        setItems(triaged);
        setCursor(0);
        setPhase("sort");
      } catch (err: any) {
        toast.show(err?.status === 429 || err?.message?.includes("limit") ? "AI limit reached" : "Couldn't analyze the photo", "error");
      }
      setAnalyzing(false);
    };
    reader.readAsDataURL(file);
  };

  // --- No-scan: hand-picked items → same 3-button triage (R-D10) ---
  const startNoScan = () => {
    const chosen = pickItems.filter((p) => pickIds.includes(p.id) && !alreadySorted.has(p.id));
    if (chosen.length === 0) {
      toast.show("Pick at least one item to sort.", "warning");
      return;
    }
    // Default everything to "belongs" — the user corrects. No AI call, no quota.
    setItems(chosen.map((p) => ({ itemId: p.id, name: p.name, bucket: "belongs" as Bucket })));
    setCursor(0);
    setPhase("sort");
  };

  const setBucket = (bucket: Bucket) => {
    setItems((prev) => prev.map((it, i) => (i === cursor ? { ...it, bucket } : it)));
  };

  // A 🔄 "wrong container" item must have a destination chosen before we advance:
  // finishPass only moves wrong_container items that carry a suggestedContainerSpaceId,
  // but the pass records EVERY item's bucket — so an unmoved 🔄 item would leave
  // history claiming a re-sort that never happened (and it'd be excluded from the
  // next pass). Only enforced when there ARE sibling containers to pick; with none
  // there's nothing to choose (the "add one in Spaces" hint covers that case).
  const current = items[cursor];
  const needsContainer =
    !!current && current.bucket === "wrong_container" && siblingContainers.length > 0 && !current.suggestedContainerSpaceId;

  const advance = () => {
    if (needsContainer) return;
    if (cursor + 1 < items.length) setCursor((c) => c + 1);
    else {
      // If any ❌ items, go to batched resolution; else straight to completion.
      const hasReject = items.some((i) => i.bucket === "doesnt_belong");
      setPhase(hasReject ? "resolve" : "done");
    }
  };

  // --- Batched ❌ resolution (R-D8) ---
  const rejects = items.filter((i) => i.bucket === "doesnt_belong");
  const canFinishResolve = rejects.every((r) => r.resolveUnsure || r.resolveSpaceId);

  const setRejectDest = (itemId: string, spaceId: string | null, unsure: boolean) => {
    setItems((prev) => prev.map((it) => (it.itemId === itemId ? { ...it, resolveSpaceId: spaceId, resolveUnsure: unsure } : it)));
  };

  // Every decided destination in this sort, grouped (🔄 → a spot inside this
  // space; ❌ with a known destination → somewhere else).
  const destinationGroups: DestinationGroup[] = React.useMemo(() => {
    const byDest = new Map<string, string[]>();
    for (const it of items) {
      const dest =
        it.bucket === "wrong_container" ? it.suggestedContainerSpaceId
        : it.bucket === "doesnt_belong" && !it.resolveUnsure ? it.resolveSpaceId
        : null;
      if (!dest) continue;
      const arr = byDest.get(dest) ?? [];
      arr.push(it.itemId);
      byDest.set(dest, arr);
    }
    return [...byDest.entries()].map(([spaceId, itemIds]) => ({
      spaceId,
      name: spaces.find((s) => s.id === spaceId)?.name ?? "Another space",
      itemIds,
    }));
  }, [items, spaces]);

  const toggleTry = (spaceId: string) => setTryDestinations((prev) => {
    const next = new Set(prev);
    if (next.has(spaceId)) next.delete(spaceId); else next.add(spaceId);
    return next;
  });

  // --- Apply everything + record the pass (idempotent: only records once) ---
  // Returns the item IDs this sort put into "Moves to make", or null on failure.
  const finishPass = async (): Promise<string[] | null> => {
    if (recording) return null;
    if (saved) return plannedItemIds; // already recorded; the plans/trials/flags were written once
    setRecording(true);
    try {
      // Decided destinations (§6b): "Sure" → planned moves (nothing moves yet);
      // "Try it" → a normal 2-week trial per destination.
      const sure = destinationGroups.filter((g) => !tryDestinations.has(g.spaceId));
      const tried = destinationGroups.filter((g) => tryDestinations.has(g.spaceId));
      let planned: string[] = [];
      if (sure.length > 0) {
        const moves = sure.flatMap((g) => g.itemIds.map((itemId) => ({ itemId, spaceId: g.spaceId })));
        const res = await apiFetch<{ planned: number; skipped: string[] }>(`/api/items/planned-moves`, {
          method: "PUT",
          body: JSON.stringify({ moves }),
        });
        const skipped = new Set(res.skipped ?? []);
        planned = moves.map((m) => m.itemId).filter((id) => !skipped.has(id));
      }
      // Each "Try it" destination becomes a Find-a-home goal with a trial, so it
      // lives where trials are already managed (confirm the move, check in,
      // keep or put back). A goal-less trial would have nowhere to be confirmed.
      for (const g of tried) {
        const first = items.find((i) => i.itemId === g.itemIds[0]);
        const what = g.itemIds.length === 1 && first ? first.name : `${g.itemIds.length} things`;
        const goal = await apiFetch<{ id: string }>(`/api/organizing-goals`, {
          method: "POST",
          body: JSON.stringify({ title: `Try ${what} in ${g.name}`.slice(0, 200), type: "find_home" }),
        });
        await apiFetch(`/api/organizing-goals/${goal.id}/items`, {
          method: "POST",
          body: JSON.stringify({ itemIds: g.itemIds }),
        });
        await apiFetch(`/api/organizing-goals/${goal.id}/trials`, {
          method: "POST",
          body: JSON.stringify({ targetSpaceId: g.spaceId, itemIds: g.itemIds }),
        });
      }
      // ❌ unsure → needs a new decision → Re-locate.
      for (const it of items.filter((i) => i.bucket === "doesnt_belong" && i.resolveUnsure)) {
        await apiFetch(`/api/items/${it.itemId}/relocate`, { method: "PUT", body: JSON.stringify({ relocate: true }) });
      }
      setPlannedItemIds(planned);
      setTriedCount(tried.reduce((n, g) => n + g.itemIds.length, 0));
      // Record the pass (append-only history). Label optional; never required.
      await apiFetch(`/api/triage-passes`, {
        method: "POST",
        body: JSON.stringify({
          spaceId,
          label: passLabel.trim() || null,
          context: contextString ?? null,
          photoKeys: photoKeys.length ? photoKeys : null,
          items: items.map((i) => ({ itemId: i.itemId, bucket: i.bucket })),
        }),
      });
      setSaved(true);
      setRecording(false);
      return planned;
    } catch (err: any) {
      toast.show(err.message || "Couldn't finish the pass", "error");
      setRecording(false);
      return null;
    }
  };

  const recap = {
    belongs: items.filter((i) => i.bucket === "belongs").length,
    toMove: destinationGroups.reduce((n, g) => n + g.itemIds.length, 0),
    decideLater: items.filter((i) => i.bucket === "doesnt_belong" && i.resolveUnsure).length,
  };

  // Record the pass. If it planned any moves, show them (the "moves" step)
  // before going on; otherwise go straight on.
  const finishThen = async (next: () => void) => {
    const planned = await finishPass();
    if (planned === null) return;
    if (planned.length > 0) { setAfterMoves(() => next); setPhase("moves"); return; }
    next();
  };

  const startAnotherPart = async () => {
    // Refresh the already-sorted set so the next part doesn't re-surface what
    // we just sorted (R-C1), and reset state.
    try {
      const r = await apiFetch<{ itemIds: string[] }>(`/api/spaces/${spaceId}/triaged-items`);
      setAlreadySorted(new Set(r.itemIds));
    } catch { /* keep prior set */ }
    setItems([]); setCursor(0); setPassLabel(""); setNoScan(false); setPickIds([]); setSaved(false);
    setTryDestinations(new Set()); setPlannedItemIds([]); setTriedCount(0);
    setPhase("capture");
  };

  const relativeTime = (iso: string) => {
    const days = Math.round((Date.now() - new Date(iso).getTime()) / 86_400_000);
    if (days <= 0) return "today";
    if (days === 1) return "yesterday";
    return `${days} days ago`;
  };

  // ---------------------------------------------------------------- render
  return (
    <div className="max-w-xl mx-auto space-y-6">
      <header>
        <button onClick={goBack} className="inline-flex items-center gap-1 text-sm text-faint hover:text-primary transition-colors mb-2">
          <ArrowLeft className="w-4 h-4" />Organize
        </button>
        <div className="flex items-center gap-2 mb-1">
          <ListChecks className="w-5 h-5 text-sage-deep" />
          <span className="text-sm font-medium text-sage-deep">Sort</span>
        </div>
        <h1 className="text-4xl font-semibold text-ink font-headline tracking-[-0.02em]">Sort out a room</h1>
        <p className="text-body mt-1 text-sm">One thing at a time. No catalog needed — just start.</p>
      </header>

      {/* Resume an INTERRUPTED sort (server-side draft). Offered in the entry
          phases only, and only until the user resumes or starts over. This is a
          real resumable draft — distinct from the last-pass hint below. */}
      {resumable && !resumeHandled && (phase === "space" || phase === "context" || phase === "capture") && (
        <div className="p-4 rounded-lg bg-amethyst-soft border border-clay-border space-y-3">
          <div className="flex items-start gap-2">
            <RotateCcw className="w-4 h-4 text-primary shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-ink">Pick up where you left off?</p>
              <p className="text-[0.8125rem] text-body mt-0.5">
                You have an unfinished sort here — <span className="tabular-nums">{resumable.items.length}</span> item{resumable.items.length === 1 ? "" : "s"}, saved {relativeTime(resumable.updatedAt)}.
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={resumeDraft}
              className="flex-1 py-2 rounded-md bg-primary text-white text-sm font-medium hover:bg-amethyst-ink transition-colors"
            >
              Resume
            </button>
            <button
              onClick={discardDraft}
              className="flex-1 py-2 rounded-md bg-white border border-clay-border text-body text-sm font-medium hover:border-primary/40 transition-colors"
            >
              Start over
            </button>
          </div>
        </div>
      )}

      {/* Resume hint (R-C7) — a hint, never a resumable scope. */}
      {lastPass && !resumable && (phase === "context" || phase === "capture") && (
        <div className="flex items-center gap-2 p-3 rounded-md bg-paper-raised border border-clay-border text-[0.8125rem] text-body">
          <RotateCcw className="w-3.5 h-3.5 text-faint shrink-0" />
          <span>Last time you sorted{lastPass.label ? <> <span className="font-medium text-ink">{lastPass.label}</span></> : " here"} · {relativeTime(lastPass.createdAt)}</span>
        </div>
      )}

      {/* ① Choose a space */}
      {phase === "space" && (
        <div className="bg-white rounded-lg border border-clay-border p-5 space-y-3">
          <label className="text-sm font-medium text-body block">Which space?</label>
          <select
            value={spaceId}
            onChange={(e) => setSpaceId(e.target.value)}
            className="w-full px-3 py-2.5 rounded-sm border border-clay-border text-sm bg-white text-body focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
          >
            <option value="">Choose a space…</option>
            {placeableSpaces.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          <button
            onClick={() => setPhase("context")}
            disabled={!spaceId}
            className="w-full py-2.5 rounded-md bg-primary text-white text-sm font-medium hover:bg-amethyst-ink transition-colors disabled:bg-clay-muted disabled:text-body disabled:cursor-not-allowed"
          >
            Continue
          </button>
        </div>
      )}

      {/* ② Context prompt — fully skippable (R-D9) */}
      {phase === "context" && (
        <div className="bg-white rounded-lg border border-clay-border p-5 space-y-4">
          <div>
            <h2 className="text-[1.0625rem] font-semibold text-ink">What bugs you most here? <span className="text-faint font-normal text-sm">(optional)</span></h2>
            <p className="text-[0.8125rem] text-faint mt-0.5">Helps us guess where things go. Skip it if you like — scanning works on its own.</p>
          </div>
          <div className="flex flex-wrap gap-2">
            {CONTEXT_CHIPS.map((chip) => {
              const on = contextChips.includes(chip);
              return (
                <button
                  key={chip}
                  onClick={() => setContextChips((prev) => (on ? prev.filter((c) => c !== chip) : [...prev, chip]))}
                  className={cn(
                    "px-3 py-1.5 rounded-pill text-[0.8125rem] font-medium transition-colors",
                    on ? "bg-amethyst-soft text-amethyst-ink" : "bg-clay-muted text-body hover:bg-clay-muted/70"
                  )}
                >
                  {chip}
                </button>
              );
            })}
          </div>
          <input
            value={contextText}
            onChange={(e) => setContextText(e.target.value)}
            placeholder="Add your own…"
            className="w-full px-3 py-2.5 rounded-sm border border-clay-border text-sm text-body placeholder:text-faint focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
          />
          <div className="flex gap-2">
            <button onClick={() => setPhase("capture")} className="flex-1 py-2.5 rounded-md bg-clay-muted text-body text-sm font-medium hover:bg-clay-muted/70 transition-colors">Skip</button>
            <button onClick={() => setPhase("capture")} className="flex-1 py-2.5 rounded-md bg-primary text-white text-sm font-medium hover:bg-amethyst-ink transition-colors">Continue</button>
          </div>
        </div>
      )}

      {/* ③ Capture (scan OR no-scan peer) */}
      {phase === "capture" && (
        <div className="space-y-3">
          {analyzing ? (
            <div className="flex flex-col items-center justify-center py-12 bg-white rounded-lg border border-clay-border">
              <Loader2 className="w-6 h-6 text-sage-deep animate-spin mb-2" />
              <p className="text-sm text-body">Looking at {space?.name ?? "the space"}…</p>
            </div>
          ) : noScan ? (
            <div className="bg-white rounded-lg border border-clay-border p-5 space-y-3">
              <div className="flex items-center justify-between">
                <h2 className="text-[1.0625rem] font-semibold text-ink">Pick what's here</h2>
                <button onClick={() => setNoScan(false)} className="text-[0.8125rem] text-faint hover:text-primary">Use a photo instead</button>
              </div>
              <InventoryPicker
                selectedIds={pickIds}
                onChange={setPickIds}
                onItemsLoaded={setPickItems}
                listMaxHeight="max-h-72"
              />
              <button
                onClick={startNoScan}
                disabled={pickIds.length === 0}
                className="w-full py-2.5 rounded-md bg-primary text-white text-sm font-medium hover:bg-amethyst-ink transition-colors disabled:bg-clay-muted disabled:text-body disabled:cursor-not-allowed"
              >
                Sort {pickIds.length > 0 ? <span className="tabular-nums">{pickIds.length} </span> : ""}selected
              </button>
            </div>
          ) : (
            <>
              {aiEnabled ? (
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="w-full flex items-center gap-3 bg-white rounded-lg border border-clay-border p-4 hover:border-sage-deep/40 transition-colors group text-left"
                >
                  <div className="w-11 h-11 rounded-md bg-sage-wash flex items-center justify-center shrink-0">
                    <Camera className="w-5 h-5 text-sage-deep" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[1.0625rem] font-semibold text-ink">Scan the space</div>
                    <div className="text-[0.8125rem] text-faint">Snap a photo — we'll pre-sort what we see</div>
                  </div>
                  <ArrowRight className="w-4 h-4 text-faint group-hover:text-sage-deep transition-colors shrink-0" />
                </button>
              ) : (
                <div className="flex items-start gap-2.5 p-3 rounded-md bg-warning-wash border border-clay-border">
                  <AlertTriangle className="w-4 h-4 text-warning shrink-0 mt-0.5" />
                  <p className="text-[0.8125rem] text-warning-text">Scanning needs AI, which is turned off. You can still pick items by hand below.</p>
                </div>
              )}
              {/* No-scan is a first-class peer (R-D10), not a buried fallback. */}
              <button
                onClick={() => setNoScan(true)}
                className="w-full flex items-center gap-3 bg-white rounded-lg border border-clay-border p-4 hover:border-primary/40 transition-colors group text-left"
              >
                <div className="w-11 h-11 rounded-md bg-amethyst-soft flex items-center justify-center shrink-0">
                  <Package className="w-5 h-5 text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[1.0625rem] font-semibold text-ink">No photo? Pick from what's here</div>
                  <div className="text-[0.8125rem] text-faint">Choose items by hand, then sort the same way</div>
                </div>
                <ArrowRight className="w-4 h-4 text-faint group-hover:text-primary transition-colors shrink-0" />
              </button>
              <input ref={fileInputRef} type="file" accept="image/*" capture="environment" onChange={handlePhoto} className="hidden" />
            </>
          )}
        </div>
      )}

      {/* ④ Sort — one item at a time, 3 buckets, one-tap override */}
      {phase === "sort" && items[cursor] && (() => {
        return (
          <div className="bg-white rounded-lg border border-clay-border p-5 space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-body">Sorting: {space?.name ?? "space"}</span>
              <button onClick={advance} disabled={needsContainer} className="text-[0.8125rem] text-faint hover:text-primary inline-flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:text-faint">Skip item<ArrowRight className="w-3 h-3" /></button>
            </div>

            <div className="text-center py-3">
              <div className="w-16 h-16 rounded-lg bg-paper-raised border border-clay-border mx-auto mb-3 flex items-center justify-center">
                <Package className="w-7 h-7 text-faint" />
              </div>
              <p className="text-[1.0625rem] font-semibold text-ink">{current.name}</p>
              <p className="text-[0.8125rem] text-sage-deep mt-1">
                <Sparkles className="w-3 h-3 inline mr-1" />AI thinks: {BUCKET_META[current.bucket].label.toLowerCase()}
              </p>
            </div>

            <div className="grid grid-cols-3 gap-2">
              {(Object.keys(BUCKET_META) as Bucket[]).map((b) => {
                const on = current.bucket === b;
                const ring = b === "belongs" ? "border-sage-deep bg-sage-wash" : b === "wrong_container" ? "border-primary bg-amethyst-soft" : "border-body bg-clay-muted";
                return (
                  <button
                    key={b}
                    onClick={() => setBucket(b)}
                    className={cn(
                      "p-3 rounded-md border text-center transition-colors",
                      on ? ring : "border-clay-border hover:border-primary/40"
                    )}
                  >
                    <span className="text-lg block">{b === "belongs" ? "✅" : b === "wrong_container" ? "🔄" : "❌"}</span>
                    <span className="text-xs font-medium text-ink block mt-1">{BUCKET_META[b].label}</span>
                  </button>
                );
              })}
            </div>
            <p className="text-[0.8125rem] text-faint text-center">{BUCKET_META[current.bucket].hint}</p>

            {/* 🔄 candidate sibling container */}
            {current.bucket === "wrong_container" && (
              <div className="p-3 rounded-md bg-amethyst-soft border border-clay-border">
                <label className="text-xs font-medium text-amethyst-ink mb-1 block">Which spot inside {space?.name ?? "this space"}?</label>
                <select
                  value={current.suggestedContainerSpaceId ?? ""}
                  onChange={(e) => setItems((prev) => prev.map((it, i) => (i === cursor ? { ...it, suggestedContainerSpaceId: e.target.value || null } : it)))}
                  className="w-full px-3 py-2 rounded-sm border border-clay-border text-sm bg-white text-body focus:outline-none focus:border-primary"
                >
                  <option value="">Pick a spot…</option>
                  {siblingContainers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
                {siblingContainers.length === 0 && <p className="text-[0.8125rem] text-faint mt-1">No spots inside {space?.name ?? "this space"} yet — you can add one in Spaces.</p>}
              </div>
            )}

            {needsContainer && (
              <p className="text-[0.8125rem] text-faint text-center">Pick a spot above so we know where it goes.</p>
            )}
            <button onClick={advance} disabled={needsContainer} className="w-full py-2.5 rounded-md bg-primary text-white text-sm font-medium hover:bg-amethyst-ink transition-colors disabled:bg-clay-muted disabled:text-body disabled:cursor-not-allowed">
              {cursor + 1 < items.length ? "Confirm" : "Confirm — that's the last one"}
            </button>

            {/* Position rail — "more to go", NEVER a percentage/denominator (R-D12) */}
            <div className="flex items-center justify-center gap-1.5 pt-1">
              {items.map((_, i) => (
                <span key={i} className={cn("w-1.5 h-1.5 rounded-pill transition-colors", i === cursor ? "bg-primary" : i < cursor ? "bg-sage" : "bg-clay-muted")} />
              ))}
            </div>
          </div>
        );
      })()}

      {/* ⑤ Batched ❌ resolution (R-D8) */}
      {phase === "resolve" && (
        <div className="bg-white rounded-lg border border-clay-border p-5 space-y-4">
          <div>
            <h2 className="text-[1.0625rem] font-semibold text-ink">Where do these go?</h2>
            <p className="text-[0.8125rem] text-faint mt-0.5">These don't belong here. Send each somewhere, or set it aside to decide later.</p>
          </div>
          <div className="space-y-3">
            {rejects.map((r) => (
              <div key={r.itemId} className="p-3 rounded-md border border-clay-border space-y-2">
                <p className="text-sm font-medium text-ink">{r.name}</p>
                <div className="flex flex-wrap items-center gap-2">
                  <select
                    value={r.resolveUnsure ? "__unsure__" : (r.resolveSpaceId ?? "")}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (v === "__unsure__") setRejectDest(r.itemId, null, true);
                      else setRejectDest(r.itemId, v || null, false);
                    }}
                    className="flex-1 min-w-[10rem] px-3 py-2 rounded-sm border border-clay-border text-sm bg-white text-body focus:outline-none focus:border-primary"
                  >
                    <option value="">Choose a destination…</option>
                    {placeableSpaces.filter((s) => s.id !== spaceId).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                    <option value="__unsure__">Not sure — decide later</option>
                  </select>
                  {r.resolveUnsure && (
                    <span className="inline-flex items-center gap-1 text-[0.8125rem] text-amethyst-ink"><RotateCcw className="w-3 h-3" />Goes to Re-locate</span>
                  )}
                </div>
              </div>
            ))}
          </div>
          <button
            onClick={() => setPhase("done") /* proceed to completion; finishPass writes everything */}
            disabled={!canFinishResolve}
            className="w-full py-2.5 rounded-md bg-primary text-white text-sm font-medium hover:bg-amethyst-ink transition-colors disabled:bg-clay-muted disabled:text-body disabled:cursor-not-allowed"
          >
            Continue
          </button>
        </div>
      )}

      {/* ⑥ Pass completion — celebrate the PASS, choose Sure / Try it per
          destination, optional label, then record. */}
      {phase === "done" && (
        <div className="bg-sage-wash rounded-lg border border-clay-border p-6 text-center space-y-4">
          {recording ? (
            <div className="py-6"><Loader2 className="w-6 h-6 text-sage-deep animate-spin mx-auto" /></div>
          ) : (
            <>
              <PackageCheck className="w-10 h-10 text-sage-deep mx-auto" />
              <div>
                <p className="text-2xl font-semibold text-ink" style={{ fontFamily: "Hanken Grotesk, system-ui, sans-serif" }}>Sorted</p>
                {/* Honest recap counts — NOT a score/percentage/denominator (R-D12). */}
                <p className="text-sm text-body mt-1 tabular-nums">
                  {recap.belongs} stay · {recap.toMove} to move · {recap.decideLater} to decide later
                </p>
              </div>

              {/* Decided destinations (§6b). Default "Sure" = a planned move in
                  Moves to make; "Try it" = a 2-week trial. Nothing moves yet. */}
              {destinationGroups.length > 0 && !saved && (
                <div className="text-left max-w-sm mx-auto space-y-2">
                  <p className="text-[0.8125rem] font-medium text-body">Where things are going</p>
                  {destinationGroups.map((g) => {
                    const trying = tryDestinations.has(g.spaceId);
                    return (
                      <div key={g.spaceId} className="flex items-center gap-2 p-2.5 rounded-md bg-white border border-clay-border">
                        <MapPin className="w-4 h-4 text-primary shrink-0" />
                        <span className="flex-1 min-w-0 text-sm text-ink truncate">
                          {g.name} <span className="text-faint tabular-nums">· {g.itemIds.length}</span>
                        </span>
                        <div role="radiogroup" aria-label={`How sure are you about ${g.name}?`} className="flex rounded-sm bg-clay-muted p-0.5 shrink-0">
                          <button
                            role="radio"
                            aria-checked={!trying}
                            onClick={() => trying && toggleTry(g.spaceId)}
                            className={cn("px-2.5 py-1 rounded-sm text-[0.8125rem] font-medium transition-colors", !trying ? "bg-white text-ink shadow-sm" : "text-body")}
                          >
                            Sure
                          </button>
                          <button
                            role="radio"
                            aria-checked={trying}
                            onClick={() => !trying && toggleTry(g.spaceId)}
                            className={cn("px-2.5 py-1 rounded-sm text-[0.8125rem] font-medium transition-colors", trying ? "bg-white text-ink shadow-sm" : "text-body")}
                          >
                            Try it
                          </button>
                        </div>
                      </div>
                    );
                  })}
                  <p className="text-[0.75rem] text-faint">Sure: added to Moves to make. Try it: a 2-week trial you check in on.</p>
                </div>
              )}

              {/* Optional free-text label — never required, no autocomplete (R-C6 guardrail). */}
              <div className="text-left max-w-xs mx-auto">
                <label className="text-[0.8125rem] font-medium text-body mb-1 block">Name this pass? <span className="text-faint font-normal">(optional)</span></label>
                <input
                  value={passLabel}
                  onChange={(e) => setPassLabel(e.target.value)}
                  placeholder="e.g. top shelf"
                  className="w-full px-3 py-2.5 rounded-sm border border-clay-border text-sm text-body placeholder:text-faint bg-white focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                />
              </div>

              {/* Recording is idempotent — the plans/trials/flags + pass are
                  written once regardless of which button lands here. */}
              <div className="space-y-2 max-w-xs mx-auto">
                <button
                  onClick={() => finishThen(() => navigate("/organize"))}
                  className="w-full py-2.5 rounded-md bg-sage-deep text-white text-sm font-medium hover:bg-sage-deep-hover transition-colors inline-flex items-center justify-center gap-1.5"
                >
                  <Check className="w-4 h-4" />Finish
                </button>
                <button
                  onClick={() => finishThen(startAnotherPart)}
                  className="w-full py-2 text-sm text-body hover:text-amethyst-ink transition-colors"
                >
                  Sort another part
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {/* ⑦ This sort's Moves to make — decided, not yet done (§6b). */}
      {phase === "moves" && (
        <div className="space-y-4">
          <div>
            <h2 className="text-[1.0625rem] font-semibold text-ink">Moves to make</h2>
            <p className="text-[0.8125rem] text-faint mt-0.5">
              Take these where they go, then mark them moved. They'll wait in Organize if you'd rather do it later.
              {triedCount > 0 && <> Things you're trying out are in your goals.</>}
            </p>
          </div>
          <MovesToMake itemIds={plannedItemIds} emptyText="All moved. Nice work." />
          <div className="flex gap-2">
            <button
              onClick={() => { const next = afterMoves; setAfterMoves(null); (next ?? (() => navigate("/organize")))(); }}
              className="flex-1 py-2.5 rounded-md bg-primary text-white text-sm font-medium hover:bg-amethyst-ink transition-colors"
            >
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
